import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, CalendarDays, MapPin } from "lucide-react";
import { NeuCard } from "../../components/neu/Neu";
import { usePrefs } from "../../context/PrefsContext";
import { formatDate } from "../../lib/format";
import { HeatAlert } from "./HeatAlert";
import { WeatherTiles, conditionOf } from "./WeatherTiles";

const CARD_3D = {
  boxShadow:
    "0 14px 28px rgba(0,0,0,0.32), 0 8px 12px rgba(0,0,0,0.20),"
    + " inset 0 1.5px 0 rgba(255,255,255,0.28), inset 0 -4px 10px rgba(0,0,0,0.18)",
};

export const WeatherCard = ({ branch, weather }) => {
  const { t, i18n } = useTranslation();
  const { reportDaylight } = usePrefs();
  useEffect(() => {
    if (weather && typeof weather.is_day === "boolean") reportDaylight(weather.is_day);
  }, [weather, reportDaylight]);
  const today = formatDate(new Date(), i18n.language);
  const { icon: CondIcon, key: condKey } = conditionOf(weather?.code ?? 0, weather?.is_day ?? true);

  return (
    <NeuCard style={CARD_3D} className="animate-rise-in overflow-hidden p-0" data-testid="weather-card">
      <HeatAlert temp={weather?.temp} />

      <div className="px-4 pb-3 pt-3">
        <div className="flex items-start justify-between gap-3">
          <div className="grid min-w-0 grid-cols-[1.5rem_1fr] items-center gap-x-2 gap-y-1.5">
            <MapPin className="h-4 w-4 justify-self-center text-primary" />
            <span className="min-w-0 truncate text-[13px] font-bold" data-testid="weather-location">
              {weather?.place || branch?.name || "—"}
              {weather?.source === "device" ? (
                <span className="ms-1 text-[10px] font-bold text-primary">{t("weather.myLocation")}</span>
              ) : null}
            </span>
            <CalendarDays className="h-4 w-4 justify-self-center text-muted-foreground" />
            <span className="text-[13px] font-bold text-muted-foreground" data-testid="weather-date">
              {today}
            </span>
            <CondIcon className="h-6 w-6 justify-self-center text-primary" />
            <span className="text-[13px] font-bold text-muted-foreground">{t(`weather.code.${condKey}`)}</span>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-1">
            <p className="font-mono text-4xl font-black leading-none text-primary" data-testid="weather-temp">
              {weather ? `${weather.temp}°` : "—"}
            </p>
            {weather ? (
              <span className="flex flex-col items-end gap-0.5 text-[13px] font-bold" data-testid="weather-range">
                <span className="text-muted-foreground">
                  {t("weather.feelsLike")} {weather.feels_like}°
                </span>
                <span className="flex items-center gap-2 font-mono">
                  <span className="flex items-center text-red-400">
                    <ArrowUp className="h-3.5 w-3.5" />
                    {weather.high}°
                  </span>
                  <span className="flex items-center text-sky-400">
                    <ArrowDown className="h-3.5 w-3.5" />
                    {weather.low}°
                  </span>
                </span>
              </span>
            ) : null}
          </div>
        </div>

        {weather ? <WeatherTiles weather={weather} /> : null}
      </div>
    </NeuCard>
  );
};
